import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import { mailboxesService, type ProviderClientConfig } from '@/services/mailboxes'
import type { IntakeAgenda } from '@/types/commerce'

export interface AdminUserOption {
  id: string
  email: string
  display_name: string
}

// SA-402 D49: the agenda picker reads SA-401's intake-agendas list (the
// orders router) — a small query of its own, mirroring useWorkflowsLibrary,
// rather than useOrders(), which fetches the order list on mount.
export function useIntakeAgendas(enabled: boolean) {
  return useQuery<IntakeAgenda[], Error>({
    queryKey: ['intake-agendas'],
    queryFn: () => apiClient.get<IntakeAgenda[]>('/api/commerce/orders/intake-agendas'),
    staleTime: 60_000,
    enabled,
  })
}

// Owner (user) options — no reusable admin-users hook exists in the app;
// a minimal inline fetch is the sanctioned fallback (task brief).
export function useAdminUsers(): AdminUserOption[] {
  const [users, setUsers] = useState<AdminUserOption[]>([])
  useEffect(() => {
    apiClient
      .get<AdminUserOption[]>('/api/admin/users')
      .then(setUsers)
      .catch(() => {
        // Owner select just stays empty; not fatal to the page.
      })
  }, [])
  return users
}

// Provider OAuth client config, loaded on mount for each registered config id.
export function useProviderConfigs(configIds: readonly string[]): {
  configs: Record<string, ProviderClientConfig>
  update: (configId: string, cfg: ProviderClientConfig) => void
} {
  const [configs, setConfigs] = useState<Record<string, ProviderClientConfig>>({})
  const joined = configIds.join(',')

  useEffect(() => {
    let cancelled = false
    for (const configId of joined ? joined.split(',') : []) {
      mailboxesService
        .getProviderConfig(configId)
        .then((cfg) => {
          if (cancelled) return
          setConfigs((prev) => ({ ...prev, [configId]: cfg }))
        })
        .catch(() => {
          // Row still renders with blank inputs on load failure.
        })
    }
    return () => {
      cancelled = true
    }
  }, [joined])

  const update = (configId: string, cfg: ProviderClientConfig) =>
    setConfigs((prev) => ({ ...prev, [configId]: cfg }))

  return { configs, update }
}
